/**
 * Deck editor settings - persisted column visibility and UI preferences
 */
import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import type { CardRoleId } from '@/types/card';

// ============================================================================
// View Mode Types
// ============================================================================

export type DeckViewMode = 'list' | 'grid' | 'stack' | 'summary';
export type DeckGroupBy = 'type' | 'cmc' | 'color_identity';
export type DeckBoard = 'main' | 'sideboard' | 'maybeboard' | 'tokens';

export const DECK_GROUP_BY_OPTIONS: { value: DeckGroupBy; label: string }[] = [
  { value: 'type', label: 'Type' },
  { value: 'cmc', label: 'Mana Cost' },
  { value: 'color_identity', label: 'Color' },
];

// ============================================================================
// Column Types
// ============================================================================

export type DeckColumnKey =
  | 'supertype'
  | 'type'
  | 'subtype'
  | 'fullType'
  | 'manaCost'
  | 'cmc'
  | 'colorIdentity'
  | 'manaProduced'
  | 'rarity'
  | 'powerToughness'
  | 'power'
  | 'toughness'
  | 'price'
  | 'set'
  | 'setNumber'
  | 'collectorNumber'
  | 'released'
  | 'roles'
  | 'keywords';

export interface DeckColumnVisibility {
  supertype: boolean;
  type: boolean;
  subtype: boolean;
  fullType: boolean;
  manaCost: boolean;
  cmc: boolean;
  colorIdentity: boolean;
  manaProduced: boolean;
  rarity: boolean;
  powerToughness: boolean;
  power: boolean;
  toughness: boolean;
  price: boolean;
  set: boolean;
  setNumber: boolean;
  collectorNumber: boolean;
  released: boolean;
  roles: boolean;
  keywords: boolean;
}

export interface DeckEditorSettings {
  columnVisibility: DeckColumnVisibility;
}

// ============================================================================
// Defaults
// ============================================================================

export const DEFAULT_COLUMN_VISIBILITY: DeckColumnVisibility = {
  supertype: false,
  type: true,
  subtype: false,
  fullType: false,
  manaCost: false,
  cmc: true,
  colorIdentity: true,
  manaProduced: false,
  rarity: false,
  powerToughness: false,
  power: false,
  toughness: false,
  price: false,
  set: false,
  setNumber: false,
  collectorNumber: false,
  released: false,
  roles: false,
  keywords: false,
};

export const DEFAULT_DECK_EDITOR_SETTINGS: DeckEditorSettings = {
  columnVisibility: DEFAULT_COLUMN_VISIBILITY,
};

// ============================================================================
// Atoms
// ============================================================================

/** Persisted deck editor settings */
export const deckEditorSettingsAtom = atomWithStorage<DeckEditorSettings>('mtgcraft:deck-editor-settings', DEFAULT_DECK_EDITOR_SETTINGS);

/** Current deck view mode - persisted to localStorage */
export const deckViewModeAtom = atomWithStorage<DeckViewMode>('mtgcraft:deck-view-mode', 'list');

/** Unified grouping preference for all views - persisted to localStorage */
export const deckGroupByAtom = atomWithStorage<DeckGroupBy>('mtgcraft:deck-group-by', 'type');

/** Selected board (main, sideboard, maybeboard) - persisted to localStorage */
export const selectedBoardAtom = atomWithStorage<DeckBoard>('mtgcraft:deck-board', 'main');

/** Role filter state - selected roles to filter by (session only, not persisted) */
export const deckRoleFilterAtom = atomWithStorage<CardRoleId[]>('mtgcraft:deck-role-filter', []);

/** Keyword filter state - selected keywords to filter by */
export const deckKeywordFilterAtom = atomWithStorage<string[]>('mtgcraft:deck-keyword-filter', []);

/** Ephemeral deck editor quick-search query (NOT persisted — resets on reload) */
export const deckSearchQueryAtom = atom<string>('');

// ============================================================================
// Column Metadata
// ============================================================================

export const DECK_COLUMN_LABELS: Record<DeckColumnKey, string> = {
  supertype: 'Supertype',
  type: 'Type',
  subtype: 'Subtype',
  fullType: 'Full Type',
  manaCost: 'Mana Cost',
  cmc: 'CMC',
  colorIdentity: 'CID',
  manaProduced: 'Produces',
  rarity: 'Rarity',
  powerToughness: 'P/T',
  power: 'Power',
  toughness: 'Toughness',
  price: 'Price',
  set: 'Set',
  setNumber: 'Set #',
  collectorNumber: '#',
  released: 'Released',
  roles: 'Roles',
  keywords: 'Keywords',
};

/** Column groups for display in visibility dropdown */
export const DECK_COLUMN_GROUPS: { label: string; columns: DeckColumnKey[] }[] = [
  { label: 'Types', columns: ['supertype', 'type', 'subtype', 'fullType'] },
  { label: 'Roles & Keywords', columns: ['roles', 'keywords'] },
  { label: 'Mana', columns: ['manaCost', 'cmc', 'colorIdentity', 'manaProduced'] },
  { label: 'Stats', columns: ['rarity', 'powerToughness', 'power', 'toughness'] },
  { label: 'Print', columns: ['set', 'setNumber', 'collectorNumber', 'released', 'price'] },
];

export const DECK_COLUMN_ORDER: DeckColumnKey[] = [
  'supertype',
  'type',
  'subtype',
  'fullType',
  'roles',
  'keywords',
  'manaCost',
  'cmc',
  'colorIdentity',
  'manaProduced',
  'rarity',
  'powerToughness',
  'power',
  'toughness',
  'price',
  'set',
  'setNumber',
  'collectorNumber',
  'released',
];

/** Grid column widths for CSS Grid template */
export const DECK_COLUMN_WIDTHS: Record<DeckColumnKey, string> = {
  supertype: 'minmax(3rem, 5rem)',
  type: 'minmax(5rem, 8rem)',
  subtype: 'minmax(4rem, 10rem)',
  fullType: 'minmax(14rem, 20rem)',
  manaCost: 'minmax(8rem, 10rem)',
  cmc: '2.25rem',
  colorIdentity: '7rem',
  manaProduced: '7rem',
  rarity: 'minmax(3rem, 5rem)',
  powerToughness: '2.75rem',
  power: '2.75rem',
  toughness: '2.75rem',
  price: 'minmax(3rem, 5rem)',
  set: 'minmax(12rem, 20rem)',
  setNumber: 'minmax(4rem, 6rem)',
  collectorNumber: 'minmax(2.5rem, 4rem)',
  released: '4.5rem',
  roles: 'minmax(4rem, 8rem)',
  keywords: 'minmax(5rem, 10rem)',
};

// ============================================================================
// Grid Template Helpers
// ============================================================================

/**
 * Columns that are NOT rendered as separate grid cells.
 * (Currently empty — all visible columns get a standalone grid cell.)
 */
const INLINE_COLUMNS: DeckColumnKey[] = [];

/**
 * Builds the CSS grid template columns string based on visible columns.
 * Fixed columns: qty (3rem), img (2.5rem), name (flexible), actions (5rem)
 * Dynamic columns: based on visibility settings (excludes inline-only columns)
 *
 * Uses DECK_COLUMN_WIDTHS directly — no responsive hiding.
 * Toggled columns are always visible; horizontal scroll handles overflow.
 */
export function buildGridTemplateColumns(visibility: DeckColumnVisibility): string {
  // Fixed columns: qty, img, name (with flexible width that can shrink)
  const fixedStart = ['3rem', '2.7rem', 'minmax(14rem, 1fr)'];
  // Fixed columns: actions
  const fixedEnd = ['5rem'];

  // Dynamic columns based on visibility — widths directly from constants
  const dynamicCols: string[] = [];
  for (const col of DECK_COLUMN_ORDER) {
    if (visibility[col] && !INLINE_COLUMNS.includes(col)) {
      dynamicCols.push(DECK_COLUMN_WIDTHS[col]);
    }
  }

  return [...fixedStart, ...dynamicCols, ...fixedEnd].join(' ');
}

/**
 * Returns array of visible columns in display order
 */
export function getVisibleColumns(visibility: DeckColumnVisibility): DeckColumnKey[] {
  return DECK_COLUMN_ORDER.filter((col) => visibility[col]);
}
